import { CAMPAIGN_LIFETIME_BLOCKER } from "@shared/campaign-lifetime-alerts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export type CampaignLifetimeView = {
  supported: boolean; reason: string; approved: boolean;
  accounts: Array<{
    enrollmentId: string; brandName: string; accountId: string; accountName: string; selectedEvent: string | null;
    revision?: number; events?: Array<{ id: string; label: string }>;
    enrollmentStatus?: string; eventStatus?: string; enrollmentApproved?: boolean;
    status: string; reason: string; enabled: boolean; dataThrough: string | null; lastAttemptAt: string | null;
    retainedCampaigns: number;
    campaigns: Array<{ campaignId: string; campaignName: string; status: string; reason: string;
      spend: number | null; count: number | null; dataThrough: string }>;
  }>;
};
export function CampaignLifetimeResults({ data, busy = false, onSelectEvent }: {
  data?: CampaignLifetimeView; busy?: boolean;
  onSelectEvent?: (account: CampaignLifetimeView["accounts"][number], event: string) => void;
}) {
  return <Card aria-label="Campaign lifetime rule">
    <CardHeader className="pb-3">
      <CardTitle className="text-base">Campaign lifetime: $100 with zero conversions</CardTitle>
      <CardDescription>Eligible Launcher accounts are included automatically, independently of statistical enrollment. When operationally ready,
        every campaign will be checked separately across its complete lifetime, including campaigns created before activation.
        Verified USD spend ≥ $100 and exactly zero conversions for the account’s selected event triggers this fixed rule.
        This does not change the account-window statistical rules or require calibration.</CardDescription>
    </CardHeader>
    <CardContent className="space-y-3">
      <div role="status" className="rounded-md border p-3 text-sm">
        <Badge variant="outline">{data?.supported ? "Lifetime source supported" : "Blocked — lifetime source unsupported"}</Badge>
        <p className="mt-2 text-muted-foreground">{data?.reason ?? CAMPAIGN_LIFETIME_BLOCKER}</p>
        <p className="mt-1 text-xs text-muted-foreground">Lifetime-rule approval: {data?.approved ? "approved" : "not approved"}.
          Collection, scheduling and Slack delivery retain their separate saved controls. No FX conversion is applied.</p>
      </div>
      <p className="text-xs text-muted-foreground">Enrollment is not evidence that campaigns have been checked. Missing event selections require
        exact saved Analytics evidence; no purchase default or event alias is applied.</p>
      {!data?.accounts.length ? <p className="text-sm text-muted-foreground">No Launcher accounts have been included yet. Eligible accounts are reconciled automatically.</p>
        : <div className="overflow-x-auto"><Table>
          <TableHeader><TableRow><TableHead>Brand / account</TableHead><TableHead>Selected event</TableHead>
            <TableHead>Lifetime readiness</TableHead><TableHead>Source freshness</TableHead></TableRow></TableHeader>
          <TableBody>{data.accounts.map(a => <TableRow key={a.enrollmentId}>
            <TableCell><div>{a.brandName}</div><div className="text-xs text-muted-foreground">{a.accountName} ({a.accountId})</div></TableCell>
            <TableCell className="break-all">
              <p>{a.selectedEvent ?? "Needs event configuration"}</p>
              {a.eventStatus && <p className="text-xs text-muted-foreground">{a.eventStatus.replaceAll("_", " ")}</p>}
              {onSelectEvent && <Select value={a.selectedEvent ?? ""} disabled={busy || !a.enabled || !a.events?.length}
                onValueChange={event => onSelectEvent(a, event)}>
                <SelectTrigger aria-label={`Exact lifetime event for ${a.accountName}`}><SelectValue placeholder="Select exact event" /></SelectTrigger>
                <SelectContent>{a.events?.map(event => <SelectItem key={event.id} value={event.id}>{event.label} ({event.id})</SelectItem>)}</SelectContent>
              </Select>}
              {!a.events?.length && onSelectEvent && <p className="text-xs text-muted-foreground">No supported saved event choices. Selection unavailable.</p>}
            </TableCell>
            <TableCell>
              {a.enrollmentStatus && <p className="mb-1 text-xs">Enrollment: {a.enrollmentStatus.replaceAll("_", " ")}</p>}
              <Badge variant="outline">{a.status.replaceAll("_", " ")}</Badge><p className="mt-1 text-xs text-muted-foreground">{a.reason}</p>
              {a.enrollmentApproved === false && <p className="text-xs text-muted-foreground">This enrollment identity is not approved for delivery.</p>}
              {a.retainedCampaigns > 0 && !a.campaigns.length && <p className="text-xs text-muted-foreground">{a.retainedCampaigns} retained campaigns; not current verified results.</p>}
              {a.campaigns.map(c => <div key={c.campaignId} className="mt-2 border-t pt-2 text-xs">
                <strong>{c.campaignName} ({c.campaignId})</strong> — {c.status}
                <p>Lifetime spend: {c.spend === null ? "unavailable" : `USD ${c.spend.toFixed(2)}`}; conversions: {c.count ?? "unavailable"}.</p>
                <p>{c.reason} Source through {c.dataThrough}.</p>
              </div>)}
            </TableCell>
            <TableCell className="text-xs">Data through: {a.dataThrough ?? "unavailable"}<br />Last attempt: {a.lastAttemptAt ?? "never"}</TableCell>
          </TableRow>)}</TableBody>
        </Table></div>}
    </CardContent>
  </Card>;
}
